import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react';

import Page from '../../components/Page';
import { getDomain, fetchSkidays } from '../../utils/functions';
import Button from '../../components/Button';
import Spinner from '../../components/Spinner';
import Accordion from '../../components/Accordion';
import { SkiDay } from '../../types/customer';

interface Props {
  idToken: string;
}

function SkiDaysPage(props: Props): JSX.Element {
  const pageId: { [key: string]: string } = {
    ruka: process.env.REACT_APP_SKI_DAYS_PAGE_RUKA ?? '',
    pyha: process.env.REACT_APP_SKI_DAYS_PAGE_PYHA ?? '',
  };
  const domain = getDomain();

  const { t } = useTranslation(['skidays']);

  const [results, setResults] = useState<JSX.Element | null>();
  const [errorMessage, setErrorMessage] = useState<string | null>();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function getSkiDays() {
    setErrorMessage(null);
    setResults(null);
    if (code) {
      setIsLoading(true);
      const data = await fetchSkidays(code, props.idToken);
      if (!data || data.length === 0) {
        setErrorMessage(t('errorMessage'));
      } else if (data) {
        const sortedData: { [key: string]: SkiDay[] } = {};
        data.forEach((item) => {
          const index = `${item.startYear}-${item.season}`;
          if (!sortedData[index]) {
            sortedData[index] = [];
          }
          sortedData[index].push(item);
        });

        const skiDayData = (
          <div className='my-4'>
            {Object.values(sortedData)
              .sort(sortSeasons)
              .map((item, i) => (
                <Accordion key={i} title={formatSeasonName(item[0])}>
                  <div className='border-1 border-gray-500 rounded bg-white p-5 grid auto-rows-auto grid-cols-[25%,37.5%,37.5%] md:grid-cols-[15%,42.5%,42.5%] grid-flow-row gap-y-2 text-center'>
                    <p></p>
                    <p className='font-bold'>{t('Lift rides')}</p>
                    <p className='font-bold'>{t('Ski days')}</p>
                    {item.map((skiDay, i) => (
                      <Fragment key={i}>
                        <p className='font-bold text-left'>
                          {t(skiDay.resort)}
                        </p>
                        <p>{skiDay.rides}</p>
                        <p>{skiDay.skiDays}</p>
                      </Fragment>
                    ))}
                    {item.length > 1 && (
                      <>
                        <p className='font-bold text-left'>{t('In total')} </p>
                        <p>
                          {item
                            .map((skiDay) => skiDay.rides)
                            .reduce((a, b) => a + b)}
                        </p>
                        <p>
                          {item
                            .map((skiDay) => skiDay.skiDays)
                            .reduce((a, b) => a + b)}
                        </p>
                      </>
                    )}
                  </div>
                </Accordion>
              ))}
          </div>
        );

        setResults(skiDayData);
      }
      setIsLoading(false);
    }
  }

  function sortSeasons(a: SkiDay[], b: SkiDay[]): number {
    if (a[0].startYear < b[0].startYear) {
      return 1;
    } else if (a[0].startYear === b[0].startYear && a[0].season === 'Summer') {
      return 1;
    } else {
      return -1;
    }
  }

  function formatSeasonName(skiDay: SkiDay) {
    if (skiDay.season === 'Winter') {
      return `${t('Winter season')} ${skiDay.startYear}-${skiDay.endYear}`;
    } else {
      return `${t('Summer season')} ${skiDay.startYear}`;
    }
  }

  const codeInput = (
    <div className='flex items-center justify-center mb-8'>
      <label htmlFor='code'></label>
      <input
        type='text'
        name='code'
        id='code'
        autoComplete='off'
        value={code}
        onChange={(e) => setCode(e.target.value)}
        className='p-3 w-full md:w-6/12 md:mr-4 border-b-1 border-black'
      />
      <Button onClick={getSkiDays}>{t('Search')}</Button>
    </div>
  );

  let mainContent;
  if (isLoading) {
    mainContent = (
      <div className='py-12'>
        <Spinner />
      </div>
    );
  } else if (errorMessage) {
    mainContent = <p>{errorMessage}</p>;
  } else if (results) {
    mainContent = results;
  }

  return (
    <div className='ski-days'>
      <Page
        pageId={pageId[domain]}
        mainContent={
          <>
            {codeInput} {mainContent}
          </>
        }
      />
    </div>
  );
}

export default SkiDaysPage;
